import { useState } from 'react';
import { MessageCircle, X, Send, Mail, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const SUPPORT_EMAIL = 'support@vaultride.ch';

export function SupportButton() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Compila tutti i campi');
      return;
    }

    // Create mailto link with pre-filled data
    const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`[Vault Ride] ${formData.subject}`)}&body=${encodeURIComponent(
      `${formData.message}\n\n---\nInviato da: ${user?.email || 'Utente non loggato'}\nNome: ${user?.nome || 'N/A'}`
    )}`;

    // Open email client
    window.location.href = mailtoLink;
    
    // Reset form and close
    setFormData({ subject: '', message: '' });
    setIsOpen(false);
    toast.success('Il tuo client email si aprirà per inviare il messaggio');
  };

  const quickActions = [
    { label: 'Problema con pagamento', subject: 'Problema con pagamento' },
    { label: 'Bug nell\'app', subject: 'Segnalazione bug' },
    { label: 'Richiesta funzionalità', subject: 'Suggerimento funzionalità' },
  ];

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 z-50 w-14 h-14 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 group"
        title="Assistenza"
      >
        <HelpCircle className="w-6 h-6 text-white" />
        <span className="absolute right-full mr-3 bg-[#0a0a0a] text-white text-sm py-1 px-3 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-[#262626]">
          Hai bisogno di aiuto?
        </span>
      </button>

      {/* Support Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-md max-h-[80vh] overflow-hidden shadow-2xl animate-in slide-in-from-bottom-4 sm:slide-in-from-bottom-0 sm:zoom-in-95">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#262626]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FF5F1F]/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#FF5F1F]" />
                </div>
                <div>
                  <h2 className="text-white font-semibold">Assistenza</h2>
                  <p className="text-[#666] text-xs">Ti rispondiamo entro 24h</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-[#666] hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Quick Actions */}
              <div className="mb-4">
                <p className="text-[#666] text-xs mb-2">Azioni rapide:</p>
                <div className="flex flex-wrap gap-2">
                  {quickActions.map((action) => (
                    <button
                      key={action.subject}
                      onClick={() => setFormData(prev => ({ ...prev, subject: action.subject }))}
                      className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                        formData.subject === action.subject
                          ? 'bg-[#FF5F1F]/20 border-[#FF5F1F] text-[#FF5F1F]'
                          : 'bg-[#121212] border-[#262626] text-[#9CA3AF] hover:border-[#FF5F1F]/50'
                      }`}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-white text-sm">Oggetto</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Di cosa hai bisogno?"
                    className="bg-[#121212] border-[#262626] text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-white text-sm">Messaggio</Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Descrivi il tuo problema o la tua richiesta..."
                    className="bg-[#121212] border-[#262626] text-white mt-1 min-h-[120px] resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#FF5F1F] hover:bg-[#FF5F1F]/90"
                  disabled={sending}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Invia Messaggio
                </Button>
              </form>

              {/* Direct Email */}
              <div className="mt-4 pt-4 border-t border-[#262626] text-center">
                <p className="text-[#666] text-xs mb-2">Oppure scrivici direttamente:</p>
                <a 
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-[#FF5F1F] hover:underline text-sm font-mono"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
